import { useCallback } from "react";
import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { 
    CampaignSubmissionsQueryQuery, 
    QueryCampaignSubmissionsArgs
} from "../../../../../schemaTypes";

export const campaignSubmissionsQuery = gql`
  query CampaignSubmissionsQuery(
    $id: String!
    $page: Int!
    $pageSize: Int!
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    campaignSubmissions(
      id: $id
      page: $page
      pageSize: $pageSize
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
        data {
            id
            createdAt
            customer
            response
        }
        total
        page
        pageSize
        totalPages
    }
  }
`;

type CampaignSubmissionItem = NonNullable<
  NonNullable<CampaignSubmissionsQueryQuery["campaignSubmissions"]>["data"]
>[number];

export interface WithCampaignSubmissions {
  data: CampaignSubmissionItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreSubmissions: boolean;
}

interface Props {
  variables: QueryCampaignSubmissionsArgs;
  children: (data: WithCampaignSubmissions) => React.ReactElement | null;
}

export const CampaignSubmissionsController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    CampaignSubmissionsQueryQuery,
    QueryCampaignSubmissionsArgs
  >(campaignSubmissionsQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const campaignSubmissionsData = data?.campaignSubmissions?.data || [];
  const normalizedSubmissions: CampaignSubmissionItem[] = campaignSubmissionsData
    .filter((submission): submission is CampaignSubmissionItem => Boolean(submission))
    .map((submission) => ({
      ...submission,
      customer: submission?.customer || "",
      response: submission?.response || "",
      createdAt: submission?.createdAt || "",
    }));
  const page = data?.campaignSubmissions?.page || variables.page;
  const pageSize = data?.campaignSubmissions?.pageSize || variables.pageSize;
  const total = data?.campaignSubmissions?.total || 0;
  const totalPages = data?.campaignSubmissions?.totalPages || 0;
  const hasMoreSubmissions = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreSubmissions) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.campaignSubmissions) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          campaignSubmissions: {
            ...fetchMoreResult?.campaignSubmissions,
            data: [
              ...(prev?.campaignSubmissions?.data || []),
              ...(fetchMoreResult?.campaignSubmissions?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreSubmissions]);

  return children({
    data: normalizedSubmissions,
    loading,
    loadMore,
    hasMoreSubmissions,
    page,
    pageSize,
    total,
    totalPages,
  });
};